import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import type { FanCollapsedChipData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';

// Chip stand-in for a user-collapsed peer fan. Visual is a "card
// deck" — the foreground chip sits on top of two angled backdrop
// cards that peek out behind, communicating "there's a stack in
// here, click to expand." Click toggles membership in
// ChatDagRuntimeContext.collapsedFanIds via toggleFanExpansion.
export const FanCollapsedChip: React.FC<NodeProps<FanCollapsedChipData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const { toggleFanExpansion } = usePersonaChatUI();
  const opacityStyle = useDagNodeOpacity(data.turnKey, false);
  const counts: string[] = [];
  if (data.okCount > 0) counts.push(`${data.okCount}/${data.peerCount} ok`);
  if (data.offlineCount > 0) counts.push(`${data.offlineCount} offline`);
  if (data.errorCount > 0) counts.push(`${data.errorCount} error`);

  const backdropBg = darkMode ? 'rgba(31, 41, 55, 0.85)' : 'rgba(243, 244, 246, 0.95)';
  const backdropBorder = darkMode ? 'rgba(107, 114, 128, 0.6)' : 'rgba(209, 213, 219, 0.9)';

  return (
    <div
      className="nodrag relative"
      style={{ width: 220, ...opacityStyle }}
    >
      {/* Back-most card — rotated -4° + translate(-4, 4). Peeks out
          on the lower-left. */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-md border pointer-events-none shadow-sm"
        style={{
          transform: 'rotate(-4deg) translate(-4px, 4px)',
          background: backdropBg,
          borderColor: backdropBorder,
          opacity: 0.5,
        }}
      />
      {/* Mid card — rotated +2° + translate(3, 2). Peeks on the
          upper-right. */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-md border pointer-events-none shadow-sm"
        style={{
          transform: 'rotate(2deg) translate(3px, 2px)',
          background: backdropBg,
          borderColor: backdropBorder,
          opacity: 0.7,
        }}
      />
      {/* Foreground chip — the clickable, content-bearing card. Uses
          opaque background so backdrops only show where they peek
          past the rotated edges. */}
      <div
        className={`relative px-2.5 py-2 rounded-md border text-[11px] shadow-sm cursor-pointer select-none transition-transform hover:-translate-y-0.5 hover:shadow-md ${
          darkMode
            ? 'bg-gray-800 border-gray-600/80 text-gray-200 hover:border-emerald-500/60'
            : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-500/70'
        }`}
        onClick={() => toggleFanExpansion(data.parentToolCallId)}
        title="Click to expand fan"
      >
        <Handle
          type="target"
          position={Position.Left}
          id="l"
          className="!opacity-0 !pointer-events-none"
        />
        <div className="flex items-center gap-1.5 font-semibold">
          <span className="opacity-70">▸</span>
          <span>{data.peerCount} peers</span>
          <span className="ml-auto text-[9px] uppercase tracking-wider opacity-50">
            stack
          </span>
        </div>
        {counts.length > 0 && (
          <div className="mt-0.5 text-[10px] opacity-80">{counts.join(' · ')}</div>
        )}
        {(data.totalDurationMs != null || data.totalTokens != null) && (
          <div className="mt-0.5 text-[10px] opacity-70 font-mono">
            {data.totalDurationMs != null &&
              (data.totalDurationMs < 1000
                ? `${data.totalDurationMs}ms`
                : `${(data.totalDurationMs / 1000).toFixed(1)}s`)}
            {data.totalDurationMs != null && data.totalTokens != null ? ' · ' : ''}
            {data.totalTokens != null && `${data.totalTokens.toLocaleString()} tok`}
          </div>
        )}
      </div>
    </div>
  );
};

export default FanCollapsedChip;
